var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import BemClassBuilder from '../../../core/utils/src/class-builders/bem';
import { KeyCode } from '../../../core/utils/src/dom';
import { Button } from '../../button/src';
import PositioningController from './utils/positioning-controller';
var lastGeneratedId = 0;
var nextGeneratedId = function () { return "awsui-popover-" + lastGeneratedId++; };
export var Popover = AwsUi.__addComponent('awsui-popover', {
    releaseStatus: 'stable',
    properties: {
        // Where the popover should be displayed when opened, relative to the trigger.
        // If the popover does not have enough space to open in this direction, it will automatically
        // choose a better direction based on available space.
        position: {
            type: 'string',
            valid: ['top', 'right', 'bottom', 'left'],
            defaultValue: 'right'
        },
        // Determines the maximum width the popover will be limited to.
        size: {
            type: 'string',
            valid: ['small', 'medium', 'large'],
            defaultValue: 'medium'
        },
        // Whether the dismiss button is shown in the popover body.
        dismissButton: {
            type: 'boolean',
            defaultValue: true
        },
        // The type of content inside the trigger region. Use:
        // - `text` for inline text triggers
        // - `custom` for the Polaris Button component.
        triggerType: {
            type: 'string',
            valid: ['text', 'custom'],
            defaultValue: 'text'
        },
        // Adds an `aria-label` to the dismiss button, in order to enhance accessibility.
        dismissLabel: {
            type: 'string',
            nullable: true
        },
        // Determines whether the popover should be visible to the user.
        __open: {
            type: 'boolean'
        },
        // Determines the actual position of the popover, based on available size.
        __position: {
            type: 'string',
            nullable: true,
            valid: [
                'top-center',
                'top-responsive',
                'right-top',
                'right-bottom',
                'bottom-center',
                'bottom-responsive',
                'left-top',
                'left-bottom'
            ]
        }
    },
    regions: {
        // Element that will trigger the popover when clicked.
        trigger: { isDefault: true },
        // An optional header for the popover.
        header: {},
        // Content of the popover.
        content: {}
    },
    events: {
        // Fired when the Popover content is shown.
        show: {
            bubbles: false,
            cancelable: false
        }
    },
    functions: {
        __show: function () {
            this.__open = true;
            this.__fireEvent('show');
        },
        __onDocumentClick: function (evt) {
            if (this.__open && !this.node.contains(evt.target)) {
                this.__initiallyFocusedElement = null;
                this.__open = false;
            }
        }
    },
    callbacks: {
        initialized: function () {
            var _this = this;
            this.__onDocumentClick = this.__onDocumentClick.bind(this);
            this.__show = this.__show.bind(this);
            this.__open = false;
            this.__position = null;
            this.__initiallyFocusedElement = null;
            this.__ariaLabelledById = nextGeneratedId();
            this.__positioningController = new PositioningController({
                getNode: function () { return _this.node; },
                getPreferredPosition: function () { return _this.position; },
                isOpen: function () { return _this.__open; },
                setActualPosition: function (position) {
                    _this.__position = position;
                }
            });
        },
        attached: function () {
            document.addEventListener('click', this.__onDocumentClick);
            this.__positioningController.addEventListeners();
        },
        detached: function () {
            document.removeEventListener('click', this.__onDocumentClick);
            this.__positioningController.removeEventListeners();
        }
    },
    render: function (m, h) {
        var _this = this;
        var isVisible = this.__open && (this.header || this.content);
        var setPopoverFocus = function () {
            var popoverBody = _this.node.querySelector('.awsui-popover__body');
            if (document.activeElement === popoverBody || popoverBody.contains(document.activeElement)) {
                return;
            }
            if (popoverBody && popoverBody.focus) {
                _this.__initiallyFocusedElement = document.activeElement;
                popoverBody.focus();
            }
            else {
                _this.__initiallyFocusedElement = undefined;
            }
        };
        var restoreInitialFocus = function () {
            if (_this.__initiallyFocusedElement && _this.__initiallyFocusedElement.focus) {
                _this.__initiallyFocusedElement.focus();
            }
            _this.__initiallyFocusedElement = undefined;
        };
        var componentDidUpdate = function () {
            if (_this.__open) {
                _this.__positioningController.recalculatePosition();
                if (_this.dismissButton) {
                    setPopoverFocus();
                }
            }
            else {
                _this.__position = null;
                if (_this.dismissButton) {
                    restoreInitialFocus();
                }
            }
        };
        var triggerKeyboardHandler = function (event) {
            if (event.keyCode === KeyCode.tab || event.keyCode === KeyCode.escape) {
                _this.__open = false;
            }
        };
        var popoverKeyboardHandler = function (event) {
            if (event.keyCode === KeyCode.escape) {
                var trigger_1 = _this.node.querySelector('.awsui-popover__trigger');
                trigger_1.focus();
                _this.__open = false;
            }
        };
        var resetPopoverFocus = function () {
            var popover = _this.node.querySelector('.awsui-popover__body');
            if (popover && _this.__open) {
                popover.focus();
            }
        };
        var trigger = function () {
            var triggerContent;
            if (_this.triggerType === 'text') {
                triggerContent = (AwsUi.__createElement("button", { type: "button", className: "awsui-popover__trigger-text", "aria-haspopup": "true", "aria-expanded": "" + _this.__open, "override-focus": true }, h.region('trigger', 'span')));
            }
            else {
                triggerContent = h.region('trigger', 'span');
            }
            return (AwsUi.__createElement("span", { className: "awsui-popover__trigger", onclick: _this.__show, onkeydown: triggerKeyboardHandler }, triggerContent));
        };
        var tabTrap = function () { return (AwsUi.__createElement("div", { className: "awsui-popover__tab-trap", tabindex: _this.__open && _this.dismissButton ? '0' : '-1', onfocus: resetPopoverFocus })); };
        var body = function () {
            var bodyClassName = new BemClassBuilder(_this, 'body')
                .modifier("size-" + _this.size)
                .modifier("position-" + _this.__position, _this.__position)
                .modifier('visible', isVisible)
                .toString();
            var arrowClassName = new BemClassBuilder(_this, 'arrow')
                .modifier("position-" + _this.__position, _this.__position)
                .toString();
            var mainClassName = new BemClassBuilder(_this, 'main')
                .modifier('has-dismiss-button', _this.dismissButton)
                .toString();
            var attr = {
                className: bodyClassName,
                onkeydown: popoverKeyboardHandler,
                tabindex: -1,
                role: 'dialog',
                'override-focus': true
            };
            var contentAttr = {
                className: 'awsui-popover__content'
            };
            // To ensure best compatibility with assistive technology, it is advised to only set ARIA properties
            // when they have a value.
            if (_this.dismissButton) {
                attr['aria-modal'] = 'true';
                if (_this.header) {
                    attr['aria-labelledby'] = _this.__ariaLabelledById;
                }
            }
            else {
                contentAttr['aria-live'] = 'polite';
                contentAttr['aria-atomic'] = 'true';
            }
            return (AwsUi.__createElement("div", __assign({}, attr),
                AwsUi.__createElement("div", { className: arrowClassName, "aria-hidden": "true" },
                    AwsUi.__createElement("div", { className: "awsui-popover__arrow-outer" }),
                    AwsUi.__createElement("div", { className: "awsui-popover__arrow-inner" })),
                _this.dismissButton && (AwsUi.__createElement("div", { className: "awsui-popover__dismiss" },
                    AwsUi.__createElement(Button, { variant: "icon", formAction: "none", icon: "close", className: "awsui-popover__dismiss-control", label: _this.dismissLabel, click: function () {
                            _this.__open = false;
                        } }))),
                AwsUi.__createElement("div", { className: mainClassName },
                    h.region('header', 'div', { className: 'awsui-popover__header', id: _this.__ariaLabelledById }),
                    h.region('content', 'div', contentAttr))));
        };
        return (AwsUi.__createElement("div", { className: "awsui-popover", lifecycleKey: "awsui-popover", componentDidMount: componentDidUpdate, componentDidUpdate: componentDidUpdate },
            trigger(),
            tabTrap(),
            body(),
            tabTrap()));
    }
});
//# sourceMappingURL=popover.js.map